import React, { useState } from 'react'
import {
  ListChecks, Search, Plus, Edit, Trash2, Eye, CheckCircle, XCircle,
  Clock, ListTodo, ArrowUp, ArrowDown, Camera, MessageSquare, X, CalendarDays,
} from 'lucide-react'
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
  Button, Input, Badge, Pagination,
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '../components/ui'
import Select, { SelectOption } from '../components/ui/select'
import { SearchableSelect } from '../components/ui/searchable-select'
import { LoadingTable } from '../components/ui/loading-states'
import { ErrorState } from '../components/ui/error-state'
import {
  useChecklistTemplates, useChecklistTemplate,
  useCreateChecklistTemplate, useUpdateChecklistTemplate, useDeleteChecklistTemplate,
  useCreateChecklistTemplateItem, useUpdateChecklistTemplateItem,
  useDeleteChecklistTemplateItem, useReorderChecklistTemplateItems,
  useChecklistAssignments, useCreateChecklistAssignment,
  useUpdateChecklistAssignment, useDeleteChecklistAssignment,
  useChecklistInstances, useChecklistInstance,
  type ChecklistTemplate, type ChecklistTemplateItem, type ChecklistAssignment,
  type ChecklistInstance, type ChecklistRequirement, type CreateTemplateItemInput,
  type TemplateFilters, type AssignmentFilters, type InstanceFilters,
} from '../hooks/checklists/useChecklists'
import { useDepartments, useDepartmentsForSelect } from '../hooks/useDepartments'
import { useOrganizationsForSelect } from '../hooks/organizations/useOrganizations'
import { useEmployees } from '../hooks/useEmployees'
import { usePermissions } from '../hooks/usePermissions'

// ==================== Helpers ====================

const STATUS_CONFIG: Record<string, { label: string; color: string; icon: React.ElementType }> = {
  in_progress: { label: 'Выполняется', color: 'bg-blue-100 text-blue-800', icon: Clock },
  completed: { label: 'Выполнен', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  incomplete: { label: 'Не выполнен', color: 'bg-red-100 text-red-800', icon: XCircle },
}

const ITEM_STATUS_CONFIG: Record<string, { label: string; color: string; icon: React.ElementType }> = {
  pending: { label: 'Ожидает', color: 'text-muted-foreground', icon: Clock },
  done: { label: 'Выполнен', color: 'text-green-600', icon: CheckCircle },
  not_done: { label: 'Не выполнен', color: 'text-red-600', icon: XCircle },
}

function formatDate(dateString: string) {
  return new Date(dateString).toLocaleDateString('ru-RU', {
    year: 'numeric', month: '2-digit', day: '2-digit',
  })
}

function formatDateTime(dateString: string) {
  return new Date(dateString).toLocaleString('ru-RU', {
    year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
  })
}

function employeeName(e?: { last_name: string; first_name: string; middle_name?: string | null } | null) {
  if (!e) return '—'
  return [e.last_name, e.first_name, e.middle_name].filter(Boolean).join(' ')
}

function todayLocal(offsetDays = 0): string {
  const d = new Date()
  d.setDate(d.getDate() + offsetDays)
  return d.toLocaleDateString('en-CA')
}

function StatusBadge({ status }: { status: string }) {
  const conf = STATUS_CONFIG[status]
  if (!conf) return <Badge variant="secondary">{status}</Badge>
  const Icon = conf.icon
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-medium ${conf.color}`}>
      <Icon className="h-3 w-3" />
      {conf.label}
    </span>
  )
}

function RequirementBadges({ item }: { item: { photo_requirement: ChecklistRequirement; comment_requirement: ChecklistRequirement } }) {
  return (
    <>
      {item.photo_requirement !== 'none' && (
        <Badge variant={item.photo_requirement === 'required' ? 'default' : 'outline'} className="text-xs">
          <Camera className="h-3 w-3 mr-1" />
          Фото {item.photo_requirement === 'required' ? 'обязательно' : 'опционально'}
        </Badge>
      )}
      {item.comment_requirement !== 'none' && (
        <Badge variant={item.comment_requirement === 'required' ? 'default' : 'outline'} className="text-xs">
          <MessageSquare className="h-3 w-3 mr-1" />
          Комментарий {item.comment_requirement === 'required' ? 'обязателен' : 'опционален'}
        </Badge>
      )}
    </>
  )
}

// ==================== Template Form ====================

interface DraftItem {
  title: string
  photo_requirement: ChecklistRequirement
  comment_requirement: ChecklistRequirement
}

interface TemplateFormProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  template?: ChecklistTemplate | null
  mode: 'create' | 'edit'
}

function TemplateForm({ open, onOpenChange, template, mode }: TemplateFormProps) {
  const createTemplate = useCreateChecklistTemplate()
  const updateTemplate = useUpdateChecklistTemplate()

  const [form, setForm] = useState({ name: '', description: '', is_active: true })
  const [items, setItems] = useState<DraftItem[]>([
    { title: '', photo_requirement: 'none', comment_requirement: 'none' },
  ])
  const [error, setError] = useState<string | null>(null)

  React.useEffect(() => {
    if (open) {
      setForm({
        name: template?.name || '',
        description: template?.description || '',
        is_active: template?.is_active ?? true,
      })
      setItems([{ title: '', photo_requirement: 'none', comment_requirement: 'none' }])
      setError(null)
    }
  }, [open, template])

  const updateItem = (index: number, patch: Partial<DraftItem>) => {
    setItems(prev => prev.map((it, i) => (i === index ? { ...it, ...patch } : it)))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.name.trim()) return
    setError(null)

    try {
      if (mode === 'create') {
        const filledItems: CreateTemplateItemInput[] = items
          .filter(it => it.title.trim())
          .map(it => ({
            title: it.title.trim(),
            photo_requirement: it.photo_requirement,
            comment_requirement: it.comment_requirement,
          }))
        await createTemplate.mutateAsync({
          name: form.name.trim(),
          description: form.description.trim() || undefined,
          items: filledItems,
        })
      } else if (template) {
        await updateTemplate.mutateAsync({
          id: template.id,
          data: {
            name: form.name.trim(),
            description: form.description.trim() || null,
            is_active: form.is_active,
          },
        })
      }
      onOpenChange(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : (err as { message?: string })?.message || 'Не удалось сохранить шаблон')
    }
  }

  const isLoading = createTemplate.isPending || updateTemplate.isPending

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{mode === 'create' ? 'Создать чек-лист' : 'Редактировать чек-лист'}</DialogTitle>
          <DialogDescription>
            {mode === 'create'
              ? 'Название и пункты в порядке выполнения'
              : 'Пункты редактируются через кнопку «Пункты» в списке'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-sm font-medium">Название *</label>
            <Input
              value={form.name}
              onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))}
              placeholder="Например: Открытие смены (кухня)"
              required
            />
          </div>

          <div>
            <label className="text-sm font-medium">Описание</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm(p => ({ ...p, description: e.target.value }))}
              placeholder="Для чего этот чек-лист..."
              className="flex min-h-[60px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
          </div>

          {mode === 'create' && (
            <div>
              <label className="text-sm font-medium">Пункты (порядок = порядок выполнения)</label>
              <div className="space-y-2 mt-1 max-h-[40vh] overflow-y-auto pr-1">
                {items.map((item, index) => (
                  <div key={index} className="flex items-start gap-2">
                    <span className="text-sm text-muted-foreground pt-2 w-5">{index + 1}.</span>
                    <div className="flex-1 space-y-1">
                      <Input
                        value={item.title}
                        onChange={(e) => updateItem(index, { title: e.target.value })}
                        placeholder="Текст задачи"
                      />
                      <div className="flex gap-2">
                        <Select
                          value={item.photo_requirement}
                          onChange={(e) => updateItem(index, { photo_requirement: e.target.value as ChecklistRequirement })}
                          className="text-xs"
                        >
                          <SelectOption value="none">Фото: не требуется</SelectOption>
                          <SelectOption value="optional">Фото: опционально</SelectOption>
                          <SelectOption value="required">Фото: обязательно</SelectOption>
                        </Select>
                        <Select
                          value={item.comment_requirement}
                          onChange={(e) => updateItem(index, { comment_requirement: e.target.value as ChecklistRequirement })}
                          className="text-xs"
                        >
                          <SelectOption value="none">Комментарий: не требуется</SelectOption>
                          <SelectOption value="optional">Комментарий: опционально</SelectOption>
                          <SelectOption value="required">Комментарий: обязательно</SelectOption>
                        </Select>
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setItems(prev => prev.filter((_, i) => i !== index))}
                      disabled={items.length === 1}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="mt-2"
                onClick={() => setItems(prev => [...prev, { title: '', photo_requirement: 'none', comment_requirement: 'none' }])}
              >
                <Plus className="h-4 w-4 mr-1" /> Добавить пункт
              </Button>
            </div>
          )}

          {error && <p className="text-sm text-red-600">{error}</p>}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Отмена
            </Button>
            <Button type="submit" disabled={isLoading || !form.name.trim()}>
              {isLoading ? 'Сохранение...' : mode === 'create' ? 'Создать' : 'Сохранить'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

// ==================== Item Form (в существующем шаблоне) ====================

interface ItemFormProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  templateId: string
  item?: ChecklistTemplateItem | null
}

function ItemForm({ open, onOpenChange, templateId, item }: ItemFormProps) {
  const createItem = useCreateChecklistTemplateItem()
  const updateItem = useUpdateChecklistTemplateItem()

  const [form, setForm] = useState<DraftItem & { description: string }>({
    title: '', description: '', photo_requirement: 'none', comment_requirement: 'none',
  })
  const [error, setError] = useState<string | null>(null)

  React.useEffect(() => {
    if (open) {
      setForm({
        title: item?.title || '',
        description: item?.description || '',
        photo_requirement: item?.photo_requirement || 'none',
        comment_requirement: item?.comment_requirement || 'none',
      })
      setError(null)
    }
  }, [open, item])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.title.trim()) return
    setError(null)

    const data = {
      title: form.title.trim(),
      description: form.description.trim() || undefined,
      photo_requirement: form.photo_requirement,
      comment_requirement: form.comment_requirement,
    }

    try {
      if (item) {
        await updateItem.mutateAsync({ templateId, itemId: item.id, data })
      } else {
        await createItem.mutateAsync({ templateId, data })
      }
      onOpenChange(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : (err as { message?: string })?.message || 'Не удалось сохранить пункт')
    }
  }

  const isLoading = createItem.isPending || updateItem.isPending

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{item ? 'Редактировать пункт' : 'Добавить пункт'}</DialogTitle>
          <DialogDescription>Задача и требования при выполнении</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-sm font-medium">Текст задачи *</label>
            <Input
              value={form.title}
              onChange={(e) => setForm(p => ({ ...p, title: e.target.value }))}
              placeholder="Что нужно сделать"
              required
            />
          </div>

          <div>
            <label className="text-sm font-medium">Описание</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm(p => ({ ...p, description: e.target.value }))}
              placeholder="Подробности (необязательно)..."
              className="flex min-h-[60px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium">Фото-подтверждение</label>
              <Select
                value={form.photo_requirement}
                onChange={(e) => setForm(p => ({ ...p, photo_requirement: e.target.value as ChecklistRequirement }))}
              >
                <SelectOption value="none">Не требуется</SelectOption>
                <SelectOption value="optional">Опционально</SelectOption>
                <SelectOption value="required">Обязательно</SelectOption>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium">Комментарий</label>
              <Select
                value={form.comment_requirement}
                onChange={(e) => setForm(p => ({ ...p, comment_requirement: e.target.value as ChecklistRequirement }))}
              >
                <SelectOption value="none">Не требуется</SelectOption>
                <SelectOption value="optional">Опционально</SelectOption>
                <SelectOption value="required">Обязательно</SelectOption>
              </Select>
            </div>
          </div>

          {error && <p className="text-sm text-red-600">{error}</p>}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Отмена</Button>
            <Button type="submit" disabled={isLoading || !form.title.trim()}>
              {isLoading ? 'Сохранение...' : item ? 'Сохранить' : 'Добавить'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

// ==================== Items Panel ====================

interface ItemsPanelProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  template: ChecklistTemplate
}

function ItemsPanel({ open, onOpenChange, template }: ItemsPanelProps) {
  const { data: fullTemplate, isLoading } = useChecklistTemplate(template.id)
  const deleteItem = useDeleteChecklistTemplateItem()
  const reorderItems = useReorderChecklistTemplateItems()

  const [isItemFormOpen, setIsItemFormOpen] = useState(false)
  const [editingItem, setEditingItem] = useState<ChecklistTemplateItem | null>(null)
  const [deletingItemId, setDeletingItemId] = useState<string | null>(null)

  const items: ChecklistTemplateItem[] = fullTemplate?.checklist_template_items || []

  const handleMove = async (index: number, direction: -1 | 1) => {
    const target = index + direction
    if (target < 0 || target >= items.length) return
    const ids = items.map(it => it.id)
    ;[ids[index], ids[target]] = [ids[target], ids[index]]
    try {
      await reorderItems.mutateAsync({ templateId: template.id, itemIds: ids })
    } catch (error) {
      console.error('Error reordering items:', error)
    }
  }

  const handleDeleteItem = async () => {
    if (!deletingItemId) return
    try {
      await deleteItem.mutateAsync({ templateId: template.id, itemId: deletingItemId })
      setDeletingItemId(null)
    } catch (error) {
      console.error('Error deleting item:', error)
    }
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Пункты: {template.name}</DialogTitle>
            <DialogDescription>
              Изменения не влияют на уже выданные чек-листы — они хранят снапшот пунктов на момент выдачи
            </DialogDescription>
          </DialogHeader>

          <div className="mb-4">
            <Button size="sm" onClick={() => { setEditingItem(null); setIsItemFormOpen(true) }}>
              <Plus className="h-4 w-4 mr-1" /> Добавить пункт
            </Button>
          </div>

          {isLoading ? (
            <div className="py-8 text-center text-muted-foreground">Загрузка пунктов...</div>
          ) : items.length === 0 ? (
            <div className="py-8 text-center">
              <ListTodo className="mx-auto h-12 w-12 text-muted-foreground/50" />
              <p className="mt-2 text-sm text-muted-foreground">Нет пунктов. Добавьте первый пункт.</p>
            </div>
          ) : (
            <div className="space-y-3 max-h-[60vh] overflow-y-auto">
              {items.map((item, index) => (
                <Card key={item.id}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className="text-sm font-medium text-muted-foreground">#{index + 1}</span>
                          <RequirementBadges item={item} />
                        </div>
                        <p className="text-sm font-medium">{item.title}</p>
                        {item.description && (
                          <p className="text-xs text-muted-foreground mt-1">{item.description}</p>
                        )}
                      </div>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost" size="sm" title="Выше"
                          disabled={index === 0 || reorderItems.isPending}
                          onClick={() => handleMove(index, -1)}
                        >
                          <ArrowUp className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost" size="sm" title="Ниже"
                          disabled={index === items.length - 1 || reorderItems.isPending}
                          onClick={() => handleMove(index, 1)}
                        >
                          <ArrowDown className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" title="Редактировать" onClick={() => { setEditingItem(item); setIsItemFormOpen(true) }}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" title="Удалить" onClick={() => setDeletingItemId(item.id)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>Закрыть</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ItemForm
        open={isItemFormOpen}
        onOpenChange={setIsItemFormOpen}
        templateId={template.id}
        item={editingItem}
      />

      <Dialog open={!!deletingItemId} onOpenChange={() => setDeletingItemId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Удалить пункт?</DialogTitle>
            <DialogDescription>
              Пункт перестанет попадать в новые чек-листы. История выданных не изменится.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeletingItemId(null)}>Отмена</Button>
            <Button variant="destructive" onClick={handleDeleteItem} disabled={deleteItem.isPending}>
              {deleteItem.isPending ? 'Удаление...' : 'Удалить'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

// ==================== Assignment Form (каскад) ====================

interface AssignmentFormProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

function AssignmentForm({ open, onOpenChange }: AssignmentFormProps) {
  const createAssignment = useCreateChecklistAssignment()
  const { data: templatesData } = useChecklistTemplates({ limit: 500, is_active: 'true' })
  const { data: organizations } = useOrganizationsForSelect()

  const [form, setForm] = useState({
    organization_id: '', // только для каскада UI, на бэкенд не уходит
    template_id: null as string | null,
    department_id: '',
    position_id: '',
    employee_id: null as string | null,
    periodicity: 'daily' as 'daily' | 'once',
    scheduled_date: '',
  })
  const [error, setError] = useState<string | null>(null)

  const { data: departments } = useDepartmentsForSelect(form.organization_id || null)

  // Каскад по фактическому составу: одним запросом берём активных сотрудников
  // подразделения и выводим из них список должностей. Фильтровать справочник
  // должностей по positions.department_id нельзя — это поле в данных почти
  // не заполнено (должности из 1С привязаны к организации, а не к отделу).
  const { data: departmentEmployeesData } = useEmployees(
    form.department_id
      ? { departmentId: form.department_id, isActive: true, limit: 1000 }
      : { limit: 1 },
  )
  const departmentEmployees = React.useMemo(
    () => (form.department_id ? departmentEmployeesData?.data || [] : []),
    [form.department_id, departmentEmployeesData],
  )

  const positionOptions = React.useMemo(() => {
    const seen = new Map<string, string>()
    for (const e of departmentEmployees) {
      if (e.position?.id && !seen.has(e.position.id)) {
        seen.set(e.position.id, e.position.name)
      }
    }
    return Array.from(seen, ([id, name]) => ({ id, name })).sort((a, b) =>
      a.name.localeCompare(b.name, 'ru'),
    )
  }, [departmentEmployees])

  React.useEffect(() => {
    if (open) {
      setForm({
        organization_id: '', template_id: null, department_id: '', position_id: '',
        employee_id: null, periodicity: 'daily', scheduled_date: '',
      })
      setError(null)
    }
  }, [open])

  const templateOptions = React.useMemo(
    () => (templatesData?.data || []).map(t => ({ value: t.id, label: t.name })),
    [templatesData],
  )

  const employeeOptions = React.useMemo(
    () => departmentEmployees
      .filter((e) => e.position_id === form.position_id)
      .map((e) => ({
        value: e.id,
        label: `${e.last_name} ${e.first_name}`,
        searchText: `${e.last_name} ${e.first_name} ${e.employee_number ?? ''}`,
      })),
    [departmentEmployees, form.position_id],
  )

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.template_id || !form.department_id) return
    if (form.periodicity === 'once' && !form.scheduled_date) return
    setError(null)

    try {
      await createAssignment.mutateAsync({
        template_id: form.template_id,
        department_id: form.department_id,
        position_id: form.position_id || undefined,
        employee_id: form.employee_id || undefined,
        periodicity: form.periodicity,
        scheduled_date: form.periodicity === 'once' ? form.scheduled_date : undefined,
      })
      onOpenChange(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : (err as { message?: string })?.message || 'Не удалось создать назначение')
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Назначить чек-лист</DialogTitle>
          <DialogDescription>
            Подразделение — всем его сотрудникам; уточните должность или конкретного сотрудника, чтобы сузить охват
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-sm font-medium">Чек-лист *</label>
            <SearchableSelect
              value={form.template_id}
              onChange={(v) => setForm(p => ({ ...p, template_id: v }))}
              options={templateOptions}
              placeholder="Выберите шаблон..."
            />
          </div>

          <div>
            <label className="text-sm font-medium">Организация</label>
            <Select
              value={form.organization_id}
              onChange={(e) => setForm(p => ({
                ...p, organization_id: e.target.value,
                department_id: '', position_id: '', employee_id: null,
              }))}
            >
              <SelectOption value="">Все организации</SelectOption>
              {(organizations || []).map((o: { id: string; name: string }) => (
                <SelectOption key={o.id} value={o.id}>{o.name}</SelectOption>
              ))}
            </Select>
          </div>

          <div>
            <label className="text-sm font-medium">Подразделение *</label>
            <Select
              value={form.department_id}
              onChange={(e) => setForm(p => ({ ...p, department_id: e.target.value, position_id: '', employee_id: null }))}
            >
              <SelectOption value="">Выберите подразделение</SelectOption>
              {(departments || []).map((d: { id: string; name: string }) => (
                <SelectOption key={d.id} value={d.id}>{d.name}</SelectOption>
              ))}
            </Select>
          </div>

          <div>
            <label className="text-sm font-medium">Должность</label>
            <Select
              value={form.position_id}
              onChange={(e) => setForm(p => ({ ...p, position_id: e.target.value, employee_id: null }))}
              disabled={!form.department_id}
            >
              <SelectOption value="">
                {form.department_id ? 'Все должности подразделения' : 'Сначала выберите подразделение'}
              </SelectOption>
              {positionOptions.map((p) => (
                <SelectOption key={p.id} value={p.id}>{p.name}</SelectOption>
              ))}
            </Select>
          </div>

          <div>
            <label className="text-sm font-medium">Сотрудник</label>
            {form.position_id ? (
              <SearchableSelect
                value={form.employee_id}
                onChange={(v) => setForm(p => ({ ...p, employee_id: v }))}
                options={employeeOptions}
                placeholder="Все сотрудники должности"
              />
            ) : (
              <Select value="" onChange={() => undefined} disabled>
                <SelectOption value="">Сначала выберите должность</SelectOption>
              </Select>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium">Периодичность</label>
              <Select
                value={form.periodicity}
                onChange={(e) => setForm(p => ({
                  ...p,
                  periodicity: e.target.value as 'daily' | 'once',
                  scheduled_date: '',
                }))}
              >
                <SelectOption value="daily">Ежедневно (в рабочие дни)</SelectOption>
                <SelectOption value="once">Разово (на дату)</SelectOption>
              </Select>
            </div>
            {form.periodicity === 'once' && (
              <div>
                <label className="text-sm font-medium">Дата *</label>
                <Input
                  type="date"
                  min={todayLocal()}
                  value={form.scheduled_date}
                  onChange={(e) => setForm(p => ({ ...p, scheduled_date: e.target.value }))}
                  required
                />
              </div>
            )}
          </div>

          {error && <p className="text-sm text-red-600">{error}</p>}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Отмена</Button>
            <Button
              type="submit"
              disabled={
                createAssignment.isPending || !form.template_id || !form.department_id ||
                (form.periodicity === 'once' && !form.scheduled_date)
              }
            >
              {createAssignment.isPending ? 'Назначение...' : 'Назначить'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

// ==================== Templates Tab ====================

function TemplatesTab({ canWrite }: { canWrite: boolean }) {
  const [filters, setFilters] = useState<TemplateFilters>({ page: 1, limit: 20, sort: 'created_at:desc' })
  const [searchTerm, setSearchTerm] = useState('')
  const [activeFilter, setActiveFilter] = useState('')

  const { data, isLoading, error, refetch } = useChecklistTemplates({
    ...filters,
    search: searchTerm || undefined,
    is_active: activeFilter || undefined,
  })

  const deleteTemplate = useDeleteChecklistTemplate()

  const [isCreateOpen, setIsCreateOpen] = useState(false)
  const [editingTemplate, setEditingTemplate] = useState<ChecklistTemplate | null>(null)
  const [itemsTemplate, setItemsTemplate] = useState<ChecklistTemplate | null>(null)
  const [deletingTemplate, setDeletingTemplate] = useState<ChecklistTemplate | null>(null)

  const handleDelete = async () => {
    if (!deletingTemplate) return
    try {
      await deleteTemplate.mutateAsync(deletingTemplate.id)
      setDeletingTemplate(null)
    } catch (error) {
      console.error('Error deleting template:', error)
    }
  }

  if (error) return <ErrorState title="Ошибка загрузки шаблонов" onAction={refetch} />

  return (
    <>
      <Card className="mb-4">
        <CardContent className="p-4">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Поиск по названию..."
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setFilters(p => ({ ...p, page: 1 })) }}
                className="pl-10"
              />
            </div>
            <Select value={activeFilter} onChange={(e) => { setActiveFilter(e.target.value); setFilters(p => ({ ...p, page: 1 })) }}>
              <SelectOption value="">Все статусы</SelectOption>
              <SelectOption value="true">Активные</SelectOption>
              <SelectOption value="false">Неактивные</SelectOption>
            </Select>
            {canWrite && (
              <Button onClick={() => setIsCreateOpen(true)}>
                <Plus className="h-4 w-4 mr-2" /> Создать чек-лист
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Библиотека чек-листов</CardTitle>
          {data && <CardDescription>Показано {data.data.length} из {data.total}</CardDescription>}
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <LoadingTable rows={10} />
          ) : data && data.data.length > 0 ? (
            <div className="space-y-4">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Название</TableHead>
                    <TableHead>Пунктов</TableHead>
                    <TableHead>Назначений</TableHead>
                    <TableHead>Статус</TableHead>
                    <TableHead>Создан</TableHead>
                    {canWrite && <TableHead className="text-right">Действия</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.data.map((template) => (
                    <TableRow key={template.id}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{template.name}</p>
                          {template.description && (
                            <p className="text-xs text-muted-foreground truncate max-w-[300px]">{template.description}</p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>{template.items_count ?? 0}</TableCell>
                      <TableCell>{template.assignments_count ?? template._count?.checklist_assignments ?? 0}</TableCell>
                      <TableCell>
                        <Badge variant={template.is_active ? 'default' : 'secondary'}>
                          {template.is_active ? 'Активен' : 'Неактивен'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm">{formatDate(template.created_at)}</TableCell>
                      {canWrite && (
                        <TableCell className="text-right">
                          <div className="flex items-center gap-1 justify-end">
                            <Button variant="ghost" size="sm" title="Пункты" onClick={() => setItemsTemplate(template)}>
                              <ListTodo className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="sm" title="Редактировать" onClick={() => setEditingTemplate(template)}>
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="sm" title="Деактивировать" onClick={() => setDeletingTemplate(template)}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {data.totalPages > 1 && (
                <div className="flex justify-center">
                  <Pagination
                    currentPage={data.page}
                    totalPages={data.totalPages}
                    onPageChange={(page) => setFilters(p => ({ ...p, page }))}
                  />
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8">
              <ListChecks className="mx-auto h-12 w-12 text-muted-foreground/50" />
              <h3 className="mt-2 text-sm font-semibold text-muted-foreground">Чек-листы не найдены</h3>
              <p className="mt-1 text-sm text-muted-foreground">Создайте первый шаблон чек-листа.</p>
            </div>
          )}
        </CardContent>
      </Card>

      <TemplateForm open={isCreateOpen} onOpenChange={setIsCreateOpen} mode="create" />

      <TemplateForm
        open={!!editingTemplate}
        onOpenChange={(open) => { if (!open) setEditingTemplate(null) }}
        template={editingTemplate}
        mode="edit"
      />

      {itemsTemplate && (
        <ItemsPanel
          open={!!itemsTemplate}
          onOpenChange={(open) => { if (!open) setItemsTemplate(null) }}
          template={itemsTemplate}
        />
      )}

      <Dialog open={!!deletingTemplate} onOpenChange={() => setDeletingTemplate(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Деактивировать чек-лист?</DialogTitle>
            <DialogDescription>
              «{deletingTemplate?.name}» перестанет выдаваться, его назначения тоже будут деактивированы.
              История выданных чек-листов сохранится.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeletingTemplate(null)}>Отмена</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleteTemplate.isPending}>
              {deleteTemplate.isPending ? 'Деактивация...' : 'Деактивировать'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

// ==================== Assignments Tab ====================

function AssignmentsTab({ canWrite }: { canWrite: boolean }) {
  const [filters, setFilters] = useState<AssignmentFilters>({ page: 1, limit: 20, sort: 'created_at:desc' })
  const [organizationFilter, setOrganizationFilter] = useState('')
  const [departmentFilter, setDepartmentFilter] = useState('')
  const [periodicityFilter, setPeriodicityFilter] = useState('')
  const [activeFilter, setActiveFilter] = useState('')

  const { data: organizations } = useOrganizationsForSelect()
  // Список подразделений сужается по выбранной организации
  const { data: departments } = useDepartmentsForSelect(organizationFilter || null)

  const { data, isLoading, error, refetch } = useChecklistAssignments({
    ...filters,
    department_id: departmentFilter || undefined,
    periodicity: periodicityFilter || undefined,
    is_active: activeFilter || undefined,
  })

  const deleteAssignment = useDeleteChecklistAssignment()
  const updateAssignment = useUpdateChecklistAssignment()

  const [isCreateOpen, setIsCreateOpen] = useState(false)
  const [deletingAssignment, setDeletingAssignment] = useState<ChecklistAssignment | null>(null)
  const [reschedulingAssignment, setReschedulingAssignment] = useState<ChecklistAssignment | null>(null)
  const [newDate, setNewDate] = useState('')

  const handleDelete = async () => {
    if (!deletingAssignment) return
    try {
      await deleteAssignment.mutateAsync(deletingAssignment.id)
      setDeletingAssignment(null)
    } catch (error) {
      console.error('Error deleting assignment:', error)
    }
  }

  const handleReschedule = async () => {
    if (!reschedulingAssignment || !newDate) return
    try {
      await updateAssignment.mutateAsync({ id: reschedulingAssignment.id, data: { scheduled_date: newDate } })
      setReschedulingAssignment(null)
    } catch (error) {
      console.error('Error rescheduling assignment:', error)
    }
  }

  if (error) return <ErrorState title="Ошибка загрузки назначений" onAction={refetch} />

  return (
    <>
      <Card className="mb-4">
        <CardContent className="p-4">
          <div className="flex items-center gap-4 flex-wrap">
            <Select
              value={organizationFilter}
              onChange={(e) => {
                setOrganizationFilter(e.target.value)
                setDepartmentFilter('')
                setFilters(p => ({ ...p, page: 1 }))
              }}
            >
              <SelectOption value="">Все организации</SelectOption>
              {(organizations || []).map((o: { id: string; name: string }) => (
                <SelectOption key={o.id} value={o.id}>{o.name}</SelectOption>
              ))}
            </Select>
            <Select value={departmentFilter} onChange={(e) => { setDepartmentFilter(e.target.value); setFilters(p => ({ ...p, page: 1 })) }}>
              <SelectOption value="">Все подразделения</SelectOption>
              {(departments || []).map((d: { id: string; name: string }) => (
                <SelectOption key={d.id} value={d.id}>{d.name}</SelectOption>
              ))}
            </Select>
            <Select value={periodicityFilter} onChange={(e) => { setPeriodicityFilter(e.target.value); setFilters(p => ({ ...p, page: 1 })) }}>
              <SelectOption value="">Любая периодичность</SelectOption>
              <SelectOption value="daily">Ежедневные</SelectOption>
              <SelectOption value="once">Разовые</SelectOption>
            </Select>
            <Select value={activeFilter} onChange={(e) => { setActiveFilter(e.target.value); setFilters(p => ({ ...p, page: 1 })) }}>
              <SelectOption value="">Все статусы</SelectOption>
              <SelectOption value="true">Активные</SelectOption>
              <SelectOption value="false">Неактивные</SelectOption>
            </Select>
            {canWrite && (
              <Button className="ml-auto" onClick={() => setIsCreateOpen(true)}>
                <Plus className="h-4 w-4 mr-2" /> Назначить
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Назначения</CardTitle>
          {data && <CardDescription>Показано {data.data.length} из {data.total}</CardDescription>}
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <LoadingTable rows={10} />
          ) : data && data.data.length > 0 ? (
            <div className="space-y-4">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Чек-лист</TableHead>
                    <TableHead>Охват</TableHead>
                    <TableHead>Периодичность</TableHead>
                    <TableHead>Статус</TableHead>
                    <TableHead>Создано</TableHead>
                    {canWrite && <TableHead className="text-right">Действия</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.data.map((assignment) => (
                    <TableRow key={assignment.id}>
                      <TableCell className="font-medium">{assignment.checklist_templates?.name || '—'}</TableCell>
                      <TableCell>
                        <div className="text-sm">
                          <p>{assignment.departments?.name || '—'}</p>
                          {assignment.positions && (
                            <p className="text-xs text-muted-foreground">{assignment.positions.name}</p>
                          )}
                          {assignment.employees && (
                            <p className="text-xs text-muted-foreground">{employeeName(assignment.employees)}</p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {assignment.periodicity === 'daily' ? (
                          <Badge variant="outline">Ежедневно</Badge>
                        ) : (
                          <Badge variant="outline">
                            <CalendarDays className="h-3 w-3 mr-1" />
                            {assignment.scheduled_date ? formatDate(assignment.scheduled_date) : 'Разово'}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant={assignment.is_active ? 'default' : 'secondary'}>
                          {assignment.is_active ? 'Активно' : 'Неактивно'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm">{formatDate(assignment.created_at)}</TableCell>
                      {canWrite && (
                        <TableCell className="text-right">
                          <div className="flex items-center gap-1 justify-end">
                            {assignment.is_active && assignment.periodicity === 'once' && (
                              <Button
                                variant="ghost" size="sm" title="Изменить дату"
                                onClick={() => {
                                  setReschedulingAssignment(assignment)
                                  setNewDate(assignment.scheduled_date?.slice(0, 10) || todayLocal())
                                }}
                              >
                                <CalendarDays className="h-4 w-4" />
                              </Button>
                            )}
                            {assignment.is_active && (
                              <Button variant="ghost" size="sm" title="Деактивировать" onClick={() => setDeletingAssignment(assignment)}>
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {data.totalPages > 1 && (
                <div className="flex justify-center">
                  <Pagination
                    currentPage={data.page}
                    totalPages={data.totalPages}
                    onPageChange={(page) => setFilters(p => ({ ...p, page }))}
                  />
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8">
              <ListChecks className="mx-auto h-12 w-12 text-muted-foreground/50" />
              <h3 className="mt-2 text-sm font-semibold text-muted-foreground">Назначений нет</h3>
              <p className="mt-1 text-sm text-muted-foreground">Назначьте чек-лист подразделению, должности или сотруднику.</p>
            </div>
          )}
        </CardContent>
      </Card>

      <AssignmentForm open={isCreateOpen} onOpenChange={setIsCreateOpen} />

      <Dialog open={!!deletingAssignment} onOpenChange={() => setDeletingAssignment(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Деактивировать назначение?</DialogTitle>
            <DialogDescription>
              Чек-лист «{deletingAssignment?.checklist_templates?.name}» перестанет выдаваться по этому назначению.
              Уже выданные чек-листы сохранятся.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeletingAssignment(null)}>Отмена</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleteAssignment.isPending}>
              {deleteAssignment.isPending ? 'Деактивация...' : 'Деактивировать'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!reschedulingAssignment} onOpenChange={() => setReschedulingAssignment(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Изменить дату разовой выдачи</DialogTitle>
            <DialogDescription>«{reschedulingAssignment?.checklist_templates?.name}»</DialogDescription>
          </DialogHeader>
          <Input type="date" min={todayLocal()} value={newDate} onChange={(e) => setNewDate(e.target.value)} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setReschedulingAssignment(null)}>Отмена</Button>
            <Button onClick={handleReschedule} disabled={updateAssignment.isPending || !newDate}>
              {updateAssignment.isPending ? 'Сохранение...' : 'Сохранить'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

// ==================== Instance Details ====================

interface InstanceDetailsProps {
  instanceId: string
  onClose: () => void
}

function InstanceDetails({ instanceId, onClose }: InstanceDetailsProps) {
  const { data: instance, isLoading } = useChecklistInstance(instanceId)

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose() }}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>{instance?.template_name || 'Чек-лист'}</DialogTitle>
          <DialogDescription>
            {instance && (
              <>
                {employeeName(instance.employees)} · {formatDate(instance.work_date)}
                {instance.departments?.name ? ` · ${instance.departments.name}` : ''}
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        {isLoading || !instance ? (
          <div className="py-8 text-center text-muted-foreground">Загрузка...</div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center gap-4 flex-wrap text-sm">
              <StatusBadge status={instance.status} />
              <span className="text-muted-foreground">
                Выполнено {instance.completed_items} из {instance.total_items}
              </span>
              <span className="text-muted-foreground">Выдан: {formatDateTime(instance.issued_at)}</span>
              {instance.closed_at && (
                <span className="text-muted-foreground">Закрыт: {formatDateTime(instance.closed_at)}</span>
              )}
            </div>

            <div className="space-y-3 max-h-[55vh] overflow-y-auto pr-1">
              {(instance.checklist_instance_items || []).map((item, index) => {
                const conf = ITEM_STATUS_CONFIG[item.status] || ITEM_STATUS_CONFIG.pending
                const Icon = conf.icon
                return (
                  <Card key={item.id}>
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        <Icon className={`h-5 w-5 mt-0.5 shrink-0 ${conf.color}`} />
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-medium text-muted-foreground">#{index + 1}</span>
                            <p className="text-sm font-medium">{item.title}</p>
                          </div>
                          <div className="flex items-center gap-2 mt-1 flex-wrap text-xs text-muted-foreground">
                            <span className={conf.color}>{conf.label}</span>
                            {item.completed_at && <span>· {formatDateTime(item.completed_at)}</span>}
                          </div>
                          {item.comment && (
                            <p className="mt-2 text-sm bg-muted rounded-md px-3 py-2">{item.comment}</p>
                          )}
                          {item.photo_url && (
                            <a href={item.photo_url} target="_blank" rel="noreferrer" className="inline-block mt-2">
                              <img
                                src={item.photo_url}
                                alt="Фото выполнения"
                                className="max-w-sm max-h-48 rounded-lg border object-cover"
                              />
                            </a>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )
              })}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Закрыть</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ==================== Instances Tab ====================

function InstancesTab() {
  const [filters, setFilters] = useState<InstanceFilters>({ page: 1, limit: 20, sort: 'work_date:desc' })
  const [dateFrom, setDateFrom] = useState(todayLocal(-7))
  const [dateTo, setDateTo] = useState(todayLocal())
  const [departmentFilter, setDepartmentFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [templateFilter, setTemplateFilter] = useState<string | null>(null)

  const { data: departmentsData } = useDepartments({ limit: 500 })
  const { data: templatesData } = useChecklistTemplates({ limit: 500 })

  const { data, isLoading, error, refetch } = useChecklistInstances({
    ...filters,
    date_from: dateFrom || undefined,
    date_to: dateTo || undefined,
    department_id: departmentFilter || undefined,
    status: statusFilter || undefined,
    template_id: templateFilter || undefined,
  })

  const [selectedInstanceId, setSelectedInstanceId] = useState<string | null>(null)

  const templateOptions = React.useMemo(
    () => (templatesData?.data || []).map(t => ({ value: t.id, label: t.name })),
    [templatesData],
  )

  if (error) return <ErrorState title="Ошибка загрузки выполнения" onAction={refetch} />

  return (
    <>
      <Card className="mb-4">
        <CardContent className="p-4">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => { setDateFrom(e.target.value); setFilters(p => ({ ...p, page: 1 })) }}
                className="w-[150px]"
              />
              <span className="text-muted-foreground">—</span>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => { setDateTo(e.target.value); setFilters(p => ({ ...p, page: 1 })) }}
                className="w-[150px]"
              />
            </div>
            <Select value={departmentFilter} onChange={(e) => { setDepartmentFilter(e.target.value); setFilters(p => ({ ...p, page: 1 })) }}>
              <SelectOption value="">Все подразделения</SelectOption>
              {(departmentsData?.data || []).map((d: { id: string; name: string }) => (
                <SelectOption key={d.id} value={d.id}>{d.name}</SelectOption>
              ))}
            </Select>
            <Select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setFilters(p => ({ ...p, page: 1 })) }}>
              <SelectOption value="">Все статусы</SelectOption>
              <SelectOption value="in_progress">Выполняется</SelectOption>
              <SelectOption value="completed">Выполнен</SelectOption>
              <SelectOption value="incomplete">Не выполнен</SelectOption>
            </Select>
            <div className="min-w-[200px]">
              <SearchableSelect
                value={templateFilter}
                onChange={(v) => { setTemplateFilter(v); setFilters(p => ({ ...p, page: 1 })) }}
                options={templateOptions}
                placeholder="Все чек-листы"
                emptyOption="Все чек-листы"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Выполнение чек-листов</CardTitle>
          {data && <CardDescription>Показано {data.data.length} из {data.total}</CardDescription>}
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <LoadingTable rows={10} />
          ) : data && data.data.length > 0 ? (
            <div className="space-y-4">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Дата</TableHead>
                    <TableHead>Сотрудник</TableHead>
                    <TableHead>Чек-лист</TableHead>
                    <TableHead>Подразделение</TableHead>
                    <TableHead>Прогресс</TableHead>
                    <TableHead>Статус</TableHead>
                    <TableHead className="text-right">Действия</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.data.map((instance: ChecklistInstance) => (
                    <TableRow key={instance.id}>
                      <TableCell className="text-sm">{formatDate(instance.work_date)}</TableCell>
                      <TableCell className="font-medium">{employeeName(instance.employees)}</TableCell>
                      <TableCell>{instance.template_name}</TableCell>
                      <TableCell className="text-sm">{instance.departments?.name || '—'}</TableCell>
                      <TableCell>
                        <span className={instance.completed_items === instance.total_items ? 'text-green-600 font-medium' : ''}>
                          {instance.completed_items}/{instance.total_items}
                        </span>
                      </TableCell>
                      <TableCell><StatusBadge status={instance.status} /></TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm" title="Открыть" onClick={() => setSelectedInstanceId(instance.id)}>
                          <Eye className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {data.totalPages > 1 && (
                <div className="flex justify-center">
                  <Pagination
                    currentPage={data.page}
                    totalPages={data.totalPages}
                    onPageChange={(page) => setFilters(p => ({ ...p, page }))}
                  />
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8">
              <ListChecks className="mx-auto h-12 w-12 text-muted-foreground/50" />
              <h3 className="mt-2 text-sm font-semibold text-muted-foreground">Нет выданных чек-листов</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Чек-листы выдаются автоматически при отметке прихода сотрудника.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {selectedInstanceId && (
        <InstanceDetails instanceId={selectedInstanceId} onClose={() => setSelectedInstanceId(null)} />
      )}
    </>
  )
}

// ==================== Main Page ====================

export function Checklists() {
  const { hasPermission } = usePermissions()
  const canWrite = hasPermission('checklists', 'write')

  return (
    <div className="space-y-6 flex-1 overflow-auto">
      <div className="flex items-center gap-3">
        <ListChecks className="h-8 w-8 text-primary" />
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Чек-листы</h1>
          <p className="text-muted-foreground">
            Шаблоны задач, назначения и контроль выполнения
          </p>
        </div>
      </div>

      <Tabs defaultValue={canWrite ? 'templates' : 'instances'} className="space-y-4">
        <TabsList>
          <TabsTrigger value="templates">Шаблоны</TabsTrigger>
          <TabsTrigger value="assignments">Назначения</TabsTrigger>
          <TabsTrigger value="instances">Выполнение</TabsTrigger>
        </TabsList>

        <TabsContent value="templates">
          <TemplatesTab canWrite={canWrite} />
        </TabsContent>

        <TabsContent value="assignments">
          <AssignmentsTab canWrite={canWrite} />
        </TabsContent>

        <TabsContent value="instances">
          <InstancesTab />
        </TabsContent>
      </Tabs>
    </div>
  )
}
